import { SliderData, SliderSettings } from '../types/slider';

const DB_NAME = 'before-after-sliders-db';
const DB_VERSION = 1;
const SLIDERS_STORE = 'sliders';
const SETTINGS_STORE = 'settings';
const SETTINGS_KEY = 'slider-settings';

// IndexedDBの初期化
const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // スライダーストアの作成
      if (!db.objectStoreNames.contains(SLIDERS_STORE)) {
        const sliderStore = db.createObjectStore(SLIDERS_STORE, { keyPath: 'id' });
        sliderStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // 設定ストアの作成
      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        db.createObjectStore(SETTINGS_STORE);
      }
    };
  });
};

export const saveSlider = async (slider: SliderData): Promise<void> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SLIDERS_STORE], 'readwrite');
    const store = transaction.objectStore(SLIDERS_STORE);
    const request = store.add(slider);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const getSliders = async (): Promise<SliderData[]> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SLIDERS_STORE], 'readonly');
    const store = transaction.objectStore(SLIDERS_STORE);
    const index = store.index('createdAt');
    const request = index.openCursor(null, 'prev');

    const sliders: SliderData[] = [];

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        sliders.push(cursor.value);
        cursor.continue();
      } else {
        resolve(sliders);
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export const deleteSlider = async (id: string): Promise<void> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SLIDERS_STORE], 'readwrite');
    const store = transaction.objectStore(SLIDERS_STORE);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const updateSlider = async (id: string, updates: Partial<SliderData>): Promise<void> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SLIDERS_STORE], 'readwrite');
    const store = transaction.objectStore(SLIDERS_STORE);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const slider = getRequest.result;
      if (slider) {
        const updatedSlider = { ...slider, ...updates };
        const putRequest = store.put(updatedSlider);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        reject(new Error('Slider not found'));
      }
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
};

export const getSettings = (): SliderSettings => {
  const data = localStorage.getItem(SETTINGS_KEY);
  return data ? JSON.parse(data) : {
    handleColor: '#3B82F6',
    handleSize: 48,
    dividerWidth: 4
  };
};

export const saveSettings = (settings: SliderSettings): void => {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
};
